import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MailCheck } from 'lucide-react';
import { buildApiUrl, apiRequest } from '../config/api';
import { getStoredUser, logoutSession } from '../api/session';
import '../pages/SalesManager/SalesDesk.css';
import './Modal.css';

const SKIP_KEY = 'hubimmo_email_check_later';
// Uniquement dans les espaces connectés (jamais sur les pages publiques)
const DASHBOARDS = /^\/(tenant|administrative|accounting|sales-manager|technician|landlord|agency-director|settings|subscription)(\/|$)/;

/**
 * Vérification de l'adresse e-mail : un code à 6 chiffres est envoyé à l'adresse (modifiable avant l'envoi).
 * Facultative par défaut : proposée une fois par session, « Plus tard » toujours possible, et rouvrable depuis le
 * profil (événement « hubimmo:verify-email »). Obligatoire seulement si le serveur l'exige (status.required).
 */
const EmailVerificationGate = () => {
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [canSkip, setCanSkip] = useState(false);

  const check = useCallback(async (force = false) => {
    const user = getStoredUser();
    if (!user || user.role === 'superadmin' || (!force && !DASHBOARDS.test(location.pathname))) { setStatus(null); return; }
    try { if (!force && sessionStorage.getItem(SKIP_KEY) === String(user.id)) return; } catch { /* stockage indisponible */ }
    try {
      const s = await apiRequest(buildApiUrl('/api/v1/auth/email-verification'), { method: 'GET' });
      setStatus(s);
      if (s?.required || s?.suggested) { setEmail(s.pendingEmail || (s.emailLooksValid ? s.email : '') || ''); setStep(s.codeSent ? 'code' : 'email'); }
    } catch { setStatus(null); }
  }, [location.pathname]);
  useEffect(() => { check(); }, [check]);
  // « Confirmer mon adresse e-mail » depuis le profil
  useEffect(() => {
    const open = () => { try { sessionStorage.removeItem(SKIP_KEY); } catch { /* stockage indisponible */ } check(true); };
    window.addEventListener('hubimmo:verify-email', open);
    return () => window.removeEventListener('hubimmo:verify-email', open);
  }, [check]);

  if (!status?.required && !status?.suggested) return null;
  const mandatory = Boolean(status.required);

  const send = async (e) => {
    e?.preventDefault();
    setBusy(true); setError('');
    try {
      await apiRequest(buildApiUrl('/api/v1/auth/email-verification/send'), { method: 'POST', body: JSON.stringify({ email: email.trim() }) });
      setStep('code'); setCode('');
    } catch (err) {
      setError(err.message || 'Envoi impossible.');
      // Envoi d'e-mails indisponible : la personne n'est pas bloquée, la vérification sera redemandée
      if (/configur|pas pu être envoyé|réessayez plus tard/i.test(err.message || '')) setCanSkip(true);
    } finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const r = await apiRequest(buildApiUrl('/api/v1/auth/email-verification/verify'), { method: 'POST', body: JSON.stringify({ code: code.trim() }) });
      try { const u = getStoredUser(); if (u) localStorage.setItem('user', JSON.stringify({ ...u, email: r.email })); } catch { /* stockage indisponible */ }
      setStatus({ ...status, required: false, suggested: false, verified: true });
    } catch (err) { setError(err.message || 'Code incorrect.'); } finally { setBusy(false); }
  };
  const later = () => { try { sessionStorage.setItem(SKIP_KEY, String(getStoredUser()?.id || '')); } catch { /* stockage indisponible */ } setStatus(null); };
  const logout = async () => { await logoutSession(); window.location.assign('/login'); };

  return (
    <div className="modal-overlay" style={{ zIndex: 15000 }} role="dialog" aria-modal="true" aria-labelledby="ev-title">
      <div className="modal modal-sm" style={{ maxWidth: 460 }}>
        <div className="modal-header"><h3 className="modal-title" id="ev-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><MailCheck size={20} /> Confirmez votre adresse e-mail</h3></div>
        <div className="modal-body">
          {step === 'email' ? (
            <form className="desk-form" onSubmit={send}>
              <p style={{ margin: 0, color: '#475569' }}>Pour sécuriser votre compte et recevoir vos documents (quittances, contrats, rapports), confirmez une adresse e-mail à laquelle vous avez accès. Vous pouvez la corriger ci-dessous.{!mandatory && ' Vous pouvez aussi le faire plus tard depuis votre profil.'}</p>
              <label className="desk-field"><span>Adresse e-mail</span><input type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="vous@exemple.com" /></label>
              {error && <p className="desk-error">{error}</p>}
              <div className="desk-form-actions">
                {mandatory && <button type="button" className="sa-outline-button" onClick={logout}>Me déconnecter</button>}
                {(canSkip || !mandatory) && <button type="button" className="sa-outline-button" onClick={later}>Plus tard</button>}
                <button type="submit" className="sa-primary-cta" disabled={busy || !email.trim()}>{busy ? 'Envoi…' : 'Recevoir le code'}</button>
              </div>
            </form>
          ) : (
            <form className="desk-form" onSubmit={verify}>
              <p style={{ margin: 0, color: '#475569' }}>Un code à 6 chiffres a été envoyé à <strong>{email}</strong>. Il est valable 15 minutes (pensez à regarder dans les courriers indésirables).</p>
              <label className="desk-field"><span>Code reçu</span><input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} required autoFocus value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} style={{ fontSize: '1.4rem', letterSpacing: '0.4em', textAlign: 'center' }} /></label>
              {error && <p className="desk-error">{error}</p>}
              <div className="desk-form-actions">
                <button type="button" className="sa-outline-button" onClick={() => { setStep('email'); setError(''); }}>Changer d’adresse</button>
                <button type="button" className="sa-outline-button" disabled={busy} onClick={send}>Renvoyer le code</button>
                {!mandatory && <button type="button" className="sa-outline-button" onClick={later}>Plus tard</button>}
                <button type="submit" className="sa-primary-cta" disabled={busy || code.length !== 6}>{busy ? 'Vérification…' : 'Valider'}</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationGate;
